/** The only way the browser reaches data. Ported from the April MVP's src/client/lib/api.ts. */
export class ApiError extends Error {
  constructor(public status: number, message: string,
              public issues?: { path: string; message: string }[]) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * The body, when there is one. The rate limiter answers in plain text rather than JSON, so this
 * cannot assume `res.json()` will parse — it used to, and a locked-out person was told "Não
 * conseguimos carregar", which describes a network problem rather than what happened.
 */
async function body(res: Response): Promise<{ message?: string; issues?: ApiError["issues"] }> {
  const text = await res.text().catch(() => "");
  if (!text) return {};
  try {
    return JSON.parse(text) as { message?: string; issues?: ApiError["issues"] };
  } catch {
    // Plain text from a middleware: use it, trimmed, rather than inventing something vaguer.
    return { message: text.slice(0, 200) };
  }
}

async function handle<T>(res: Response): Promise<T> {
  if (res.status === 401) {
    /*
     * Read the body *before* deciding what to say.
     *
     * This branch used to throw "Sessão expirada" without looking, which is right for an expired
     * cookie and wrong for the one 401 a person causes on purpose: a wrong password. The server
     * says "E-mail ou senha incorretos." (app.ts) and the login screen reported the session had
     * expired, on a session that had never started.
     */
    const { message } = await body(res);
    // Only bounce when a *session* lapsed. On /login there is nothing to bounce away from.
    if (!location.pathname.startsWith("/login")) location.href = "/login";
    throw new ApiError(401, message ?? "Sessão expirada");
  }
  if (!res.ok) {
    const { message, issues } = await body(res);
    throw new ApiError(res.status, message ?? "Não conseguimos carregar.", issues);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * A network failure is not an HTTP failure.
 *
 * `fetch` rejects with a bare `TypeError` when the request never reaches the server, and every
 * caller in this app tests for `instanceof ApiError` — so offline, the login screen rendered
 * nothing at all and a mutation failed in silence. Status 0 is the convention for "no response".
 */
const send = <T>(method: string) => async (url: string, data?: unknown): Promise<T> => {
  let res: Response;
  try {
    res = await fetch(url, {
      method,
      credentials: "include",
      ...(data === undefined ? {} : {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    });
  } catch {
    throw new ApiError(0, "Sem conexão. Verifique a internet e tente de novo.");
  }
  return handle<T>(res);
};

export const apiGet = <T>(url: string) => send<T>("GET")(url);
export const apiPost = <T>(url: string, data?: unknown) => send<T>("POST")(url, data);
export const apiPatch = <T>(url: string, data: unknown) => send<T>("PATCH")(url, data);
export const apiPut = <T>(url: string, data: unknown) => send<T>("PUT")(url, data);
export const apiDelete = <T>(url: string) => send<T>("DELETE")(url);
